<script>
    import {providerLocale} from '$lib/providerLocale.js';
    import {t} from '$utils/lang.js';
    import {waehrung} from '$utils/waehrung.js';
    import {tick} from 'svelte';

    let {
        paymentId,
        metadata,
        onApproved = () => {},
        showButton = true,
        onReady = (_api) => {},
    } = $props();

    let isLoading = $state(true);
    let isEligible = $state(true);
    let errorMessage = $state(null);
    let isSubmitting = $state(false);
    let cardFields = $state(null);
    // Spiegelt cardFields.getState().isFormValid nach außen — siehe inputEvents
    // in init(). Nur für Test-Observability, beeinflusst den Flow nicht.
    let formValid = $state(false);

    let nameEl = $state();
    let cardNumberEl = $state();
    let expiryEl = $state();
    let cvvEl = $state();
    let formEl = $state();

    const NAMESPACE = 'paypalAcdc';
    let _sdkPromise = null;

    function parsePaypalError(err) {
        try {
            const raw = typeof err === 'string' ? err : err?.message ?? '';
            const jsonStart = raw.indexOf('{');
            const json = JSON.parse(jsonStart >= 0 ? raw.slice(jsonStart) : raw);
            const detail = json?.details?.[0];
            if (detail) {
                const field = detail.field ?? '';
                const issue = detail.issue ?? '';
                const desc = (detail.description ?? '').toLowerCase();
                if (field.includes('/number') || desc.includes('card number')) return t('Ungültige Kartennummer.');
                if (field.includes('/expiry') || desc.includes('expir')) return t('Ungültiges Ablaufdatum.');
                if (field.includes('/security_code') || field.includes('/cvv') || desc.includes('security')) return t('Ungültiger Sicherheitscode.');
                if (issue === 'INSTRUMENT_DECLINED' || desc.includes('declined')) return t('Karte abgelehnt. Bitte andere Karte verwenden.');
                if (desc.includes('expired')) return t('Karte abgelaufen.');
                if (detail.description) return detail.description;
            }
            if (json?.name === 'INSTRUMENT_DECLINED') return t('Karte abgelehnt. Bitte andere Karte verwenden.');
        } catch (_) {}
        return t('Kartenzahlung fehlgeschlagen. Bitte erneut versuchen.');
    }

    function loadAcdcSdk(meta) {
        if (_sdkPromise) return _sdkPromise;
        _sdkPromise = new Promise((resolve, reject) => {
            if (window[NAMESPACE]) { resolve(window[NAMESPACE]); return; }
            const params = new URLSearchParams({
                'client-id': meta.clientId,
                currency: meta.currency ?? waehrung(),
                locale: meta.locale ?? providerLocale(),
                intent: meta.intent ?? 'capture',
                components: meta.components ?? 'card-fields,buttons',
            });
            if (meta.merchantId) params.set('merchant-id', meta.merchantId);
            if (meta['buyer-country']) params.set('buyer-country', meta['buyer-country']);
            const script = document.createElement('script');
            script.src = `https://www.paypal.com/sdk/js?${params}`;
            script.setAttribute('data-namespace', NAMESPACE);
            script.onload = () => {
                if (!window[NAMESPACE]) {
                    reject(new Error('PayPal SDK geladen, aber kein ' + NAMESPACE + ' Namespace'));
                    return;
                }
                resolve(window[NAMESPACE]);
            };
            script.onerror = () => reject(new Error(t('PayPal ACDC SDK konnte nicht geladen werden')));
            document.head.appendChild(script);
        });
        return _sdkPromise;
    }

    async function createOrder() {
        errorMessage = null;
        const params = new URLSearchParams({
            cl: 'm7-checkout-dpc',
            fnc: 'createGatewayOrder',
            payment_id: paymentId,
        });
        const res = await fetch(`/index.php?${params}`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
        });
        if (!res.ok) throw new Error('Netzwerkfehler');
        const data = await res.json();
        if (!data?.order?.id) throw new Error(data?.message ?? t('Bestellung konnte nicht erstellt werden.'));
        return data.order.id;
    }

    $effect(() => {
        const init = async () => {
            try {
                const paypal = await loadAcdcSdk(metadata);

                if (!paypal.CardFields) {
                    isEligible = false;
                    isLoading = false;
                    return;
                }

                const cf = paypal.CardFields({
                    createOrder,
                    // NOTE: do NOT trigger placeOrder from here. The CardFields
                    // onApprove callback can fire before the order has settled
                    // to APPROVED on PayPal's side, so calling placeOrder here
                    // raced executePayment against the approval (backend saw
                    // status=CREATED → notApproved). We instead signal approval
                    // from handleSubmit AFTER `await cardFields.submit()`
                    // resolves (3DS fully completed). Kept as a no-op for SDK
                    // shape compatibility.
                    onApprove: () => {},
                    onError: (err) => {
                        console.error('PayPal ACDC error:', err);
                        errorMessage = parsePaypalError(err);
                        isSubmitting = false;
                    },
                });

                if (!cf.isEligible()) {
                    isEligible = false;
                    isLoading = false;
                    return;
                }

                isLoading = false;
                await tick();
                // Pass DOM-element refs instead of CSS selectors. The app
                // lives inside the m7-checkout custom-element's shadow root,
                // so PayPal's document.querySelector('#acdc-…') from inside
                // its iframe-loader can't see the targets. render() officially
                // supports element references as a fallback for this case.

                const fieldStyle = {
                    input: {'font-size': '14px', 'color': '#111827', 'padding': '6px 12px'},
                    ':focus': {outline: 'none'},
                    '.invalid': {color: '#dc2626'},
                };
                // Formular-Gültigkeit nach außen spiegeln (data-paypal-acdc-form-valid).
                // Die Kartenfelder sind cross-origin iframes — von außen ist NICHT
                // erkennbar, ob schon vollständige Daten drinstehen. Der F28-Test
                // musste deshalb blind 90s warten. onChange + getState() liefert das
                // Signal ohne Polling; rein lesend, wrapped in try/catch, damit eine
                // SDK-Änderung den Bezahlvorgang nicht anfassen kann.
                const refreshFormValid = async () => {
                    try {
                        const s = await cf.getState();
                        formValid = !!s?.isFormValid;
                    } catch { /* getState nicht verfügbar → Attribut bleibt false */ }
                };
                const inputEvents = {onChange: refreshFormValid, onBlur: refreshFormValid};

                await Promise.all([
                    cf.NameField({placeholder: t('Max Mustermann'), style: fieldStyle, inputEvents}).render(nameEl),
                    cf.NumberField({placeholder: '4111 1111 1111 1111', style: fieldStyle, inputEvents}).render(cardNumberEl),
                    cf.ExpiryField({placeholder: 'MM/JJ', style: fieldStyle, inputEvents}).render(expiryEl),
                    cf.CVVField({placeholder: '···', style: fieldStyle, inputEvents}).render(cvvEl),
                ]);

                cardFields = cf;
                onReady({
                    submit: () => formEl?.requestSubmit(),
                    get isSubmitting() { return isSubmitting; },
                });
            } catch (err) {
                isLoading = false;
                errorMessage = t('Kreditkarte konnte nicht geladen werden. Bitte Seite neu laden.');
                console.error('PayPal ACDC load error:', err);
            }
        };

        init();
    });

    // Map PayPal CardFields getState() field-validity to a specific German
    // hint, so the user sees what to fix instead of a generic round-trip error.
    function validityMessage(fields) {
        if (!fields) return t('Bitte prüfe deine Kartendaten.');
        const invalid = (f) => f && (f.isEmpty || f.isValid === false);
        if (invalid(fields.cardNumberField)) return t('Bitte gib eine gültige Kartennummer ein.');
        if (invalid(fields.cardExpiryField)) return t('Bitte gib ein gültiges Ablaufdatum ein.');
        if (invalid(fields.cardCvvField)) return t('Bitte gib den Sicherheitscode (CVC) ein.');
        if (invalid(fields.cardNameField)) return t('Bitte gib den Karteninhaber ein.');
        return t('Bitte prüfe deine Kartendaten (Kartennummer, Ablaufdatum und Sicherheitscode).');
    }

    async function handleSubmit(e) {
        e.preventDefault();
        if (!cardFields || isSubmitting) return;
        errorMessage = null;
        // Client-side validation BEFORE hitting PayPal/backend: an empty or
        // invalid field otherwise lets cardFields.submit() fail → onApprove
        // never fires → backend executePayment sees order=CREATED → confusing
        // notApproved round-trip. getState() reports per-field validity.
        try {
            const state = await cardFields.getState();
            if (state && state.isFormValid === false) {
                errorMessage = validityMessage(state.fields);
                return;
            }
        } catch (_) {
            // getState unavailable (SDK shape change) → fall through and let
            // submit() + onError surface any problem rather than blocking.
        }
        isSubmitting = true;
        try {
            // Await the FULL card submission incl. 3DS. This promise resolves
            // only once authentication completed and the order reached
            // APPROVED — only THEN do we signal the caller to placeOrder, so
            // executePayment never races a still-CREATED order. A rejection
            // (3DS aborted / declined) skips placeOrder and shows the error.
            await cardFields.submit({contingencies: ['3D_SECURE']});
            onApproved();
            // Leave isSubmitting=true: onApproved drives gatewayApproved →
            // this component unmounts. If placeOrder fails it remounts fresh
            // (isSubmitting=false), so we don't reset here.
        } catch (err) {
            console.error('PayPal ACDC submit error:', err);
            if (!(err?.message ?? '').includes('Window closed')) {
                errorMessage = parsePaypalError(err);
            }
            isSubmitting = false;
        }
    }
</script>

<div data-paypal-acdc-state={isLoading ? 'loading' : (!isEligible ? 'ineligible' : 'ready')} data-paypal-acdc-approved={String(!!cardFields)} data-paypal-acdc-form-valid={String(formValid)} data-paypal-acdc-error={errorMessage ?? ''}>
    {#if isLoading}
        <div class="space-y-3">
            <div class="h-14 bg-gray-100 animate-pulse rounded"></div>
            <div class="h-14 bg-gray-100 animate-pulse rounded"></div>
            <div class="grid grid-cols-2 gap-3">
                <div class="h-14 bg-gray-100 animate-pulse rounded"></div>
                <div class="h-14 bg-gray-100 animate-pulse rounded"></div>
            </div>
            <div class="h-14 bg-gray-100 animate-pulse rounded"></div>
        </div>
    {:else if !isEligible}
        <p class="text-gray-103 text-sm">{t('Kreditkartenzahlung ist für diesen Warenkorb nicht verfügbar.')}</p>
    {:else}
        <form bind:this={formEl} onsubmit={handleSubmit} class="space-y-3">
            <div>
                <label class="block text-xs text-gray-103 mb-1" for="acdc-card-name">{t('Karteninhaber')}</label>
                <div id="acdc-card-name" bind:this={nameEl} class="rounded-md bg-white overflow-hidden h-12"></div>
            </div>
            <div>
                <label class="block text-xs text-gray-103 mb-1" for="acdc-card-number">{t('Kartennummer')}</label>
                <div id="acdc-card-number" bind:this={cardNumberEl}
                    class="rounded-md bg-white overflow-hidden h-12"
                ></div>
            </div>
            <div class="grid grid-cols-2 gap-3">
                <div>
                    <label class="block text-xs text-gray-103 mb-1" for="acdc-expiry">{t('Gültig bis')}</label>
                    <div id="acdc-expiry" bind:this={expiryEl}
                        class="rounded-md bg-white overflow-hidden h-12"
                    ></div>
                </div>
                <div>
                    <label class="block text-xs text-gray-103 mb-1" for="acdc-cvv">{t('Sicherheitscode')}</label>
                    <div id="acdc-cvv" bind:this={cvvEl}
                        class="rounded-md bg-white overflow-hidden h-12"
                    ></div>
                </div>
            </div>
            {#if showButton}
            <button
                type="submit"
                disabled={isSubmitting || !cardFields}
                class="w-full h-10 rounded-md font-bold text-sm transition-opacity bg-[#003087] text-white
                    {isSubmitting || !cardFields ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'}"
            >
                {isSubmitting ? t('Wird verarbeitet…') : t('Karte bestätigen')}
            </button>
            {/if}
        </form>
    {/if}

    {#if errorMessage}
        <p class="text-scarlet text-sm mt-2">{errorMessage}</p>
    {/if}
</div>
